// Author: Ozy
// Image Editor - Advanced image editing for product uploads
// Features: Crop, Rotate, Flip, Brightness, Contrast, Saturation, Presets, Auto-enhance
// Extensible tool registry pattern for future expansion

(function() {
  'use strict';

  // ============================================================================
  // TOOL REGISTRY - Extensible plugin architecture
  // ============================================================================
  class ToolRegistry {
    constructor() {
      this.tools = new Map();
      this.categories = ['transform', 'adjust', 'effects', 'ai'];
    }

    register(toolId, config) {
      this.tools.set(toolId, {
        id: toolId,
        name: config.name,
        icon: config.icon,
        category: config.category || 'transform',
        shortcut: config.shortcut,
        panel: config.panel,
        onActivate: config.onActivate || (() => {}),
        onDeactivate: config.onDeactivate || (() => {}),
        apply: config.apply || (() => {}),
        reset: config.reset || (() => {}),
        ...config
      });
      console.log(`[ToolRegistry] Registered tool: ${toolId}`);
    }

    get(toolId) {
      return this.tools.get(toolId);
    }

    getAll() {
      return Array.from(this.tools.values());
    }

    getByCategory(category) {
      return this.getAll().filter(t => t.category === category);
    }
  }

  // ============================================================================
  // IMAGE EDITOR - Main controller class
  // ============================================================================
  class ImageEditor {
    constructor(container) {
      this.container = container;
      this.registry = new ToolRegistry();
      
      // State
      this.state = {
        imageUrl: null,
        originalWidth: 0,
        originalHeight: 0,
        displayWidth: 0,
        displayHeight: 0,
        activeTool: 'crop',
        isMobile: window.innerWidth <= 768,
        panelOpen: false,
        
        // Requirements from URL params
        productId: null,
        productHandle: null,
        slot: 1,
        returnUrl: null,
        requirements: null,
        
        // Edit state
        crop: { x: 0, y: 0, width: 0, height: 0 },
        cropInitialized: false, // Track if crop has been set up with proper aspect ratio
        rotation: 0,           // Total rotation (base + fine)
        baseRotation: 0,       // 0, 90, 180, or 270
        fineRotation: 0,       // -45 to +45
        flip: { horizontal: false, vertical: false },
        adjustments: { brightness: 0, contrast: 0, saturation: 0 },
        preset: 'original',
        
        // Validation
        meetsResolution: true,
        isLowRes: false
      };

      // DOM elements
      this.elements = {};
      
      // API configuration
      this.apiUrl = container.dataset.apiUrl || 'https://sdoac.seraphinagrey.com';
      this.shopDomain = container.dataset.shopDomain;
      
      this.init();
    }

    init() {
      console.log('[ImageEditor] Initializing...');
      
      // Detect and apply theme
      this.detectTheme();
      
      // Cache DOM elements
      this.cacheElements();
      
      // Register built-in tools
      this.registerTools();
      
      // Parse URL parameters
      this.parseUrlParams();
      
      // Bind events
      this.bindEvents();
      
      // Load image if URL provided
      if (this.state.imageUrl) {
        this.loadImage(this.state.imageUrl);
      }
      
      // Set initial tool
      this.setActiveTool('crop');
      
      console.log('[ImageEditor] Initialized successfully');
    }

    detectTheme() {
      // Check for site-wide theme setting
      const currentTheme = document.documentElement.getAttribute('data-theme');
      if (currentTheme) {
        this.container.setAttribute('data-theme', currentTheme);
        console.log('[ImageEditor] Using site theme:', currentTheme);
        return;
      }
      
      // Fallback: detect from background color
      const bodyBg = window.getComputedStyle(document.body).backgroundColor;
      if (bodyBg) {
        const rgb = bodyBg.match(/\d+/g);
        if (rgb && rgb.length >= 3) {
          const luminance = (parseInt(rgb[0]) + parseInt(rgb[1]) + parseInt(rgb[2])) / 3;
          const isDark = luminance < 128;
          this.container.setAttribute('data-theme', isDark ? 'dark' : 'light');
          console.log('[ImageEditor] Detected theme:', isDark ? 'dark' : 'light');
        }
      }
    }

    cacheElements() {
      this.elements = {
        // Header
        backBtn: this.container.querySelector('#editor-back-btn'),
        resetBtn: this.container.querySelector('#editor-reset-btn'),
        applyBtn: this.container.querySelector('#editor-apply-btn'),
        subtitle: this.container.querySelector('#editor-subtitle'),
        
        // Canvas
        image: this.container.querySelector('#editor-image'),
        cropOverlay: this.container.querySelector('#crop-overlay'),
        canvasWrapper: this.container.querySelector('#canvas-wrapper'),
        loading: this.container.querySelector('#editor-loading'),
        
        // Info bar
        infoDimensions: this.container.querySelector('#info-dimensions'),
        infoRequirements: this.container.querySelector('#info-requirements'),
        infoStatus: this.container.querySelector('#info-status'),
        infoStatusText: this.container.querySelector('#info-status-text'),
        resolutionWarning: this.container.querySelector('#resolution-warning'),
        warningText: this.container.querySelector('#warning-text'),
        
        // Toolbar
        toolbar: this.container.querySelector('#editor-toolbar'),
        toolButtons: this.container.querySelectorAll('.image-editor__tool'),
        
        // Panels
        panel: this.container.querySelector('#editor-panel'),
        panelCrop: this.container.querySelector('#panel-crop'),
        panelRotate: this.container.querySelector('#panel-rotate'),
        panelFlip: this.container.querySelector('#panel-flip'),
        panelAdjust: this.container.querySelector('#panel-adjust'),
        panelPresets: this.container.querySelector('#panel-presets'),
        panelAuto: this.container.querySelector('#panel-auto'),
        
        // Crop panel
        aspectDisplay: this.container.querySelector('#aspect-ratio-text'),
        showGrid: this.container.querySelector('#show-grid'),
        
        // Rotate panel
        rotateButtons: this.container.querySelectorAll('.image-editor__rotate-btn'),
        rotationSlider: this.container.querySelector('#rotation-slider'),
        rotationValue: this.container.querySelector('#rotation-value'),
        
        // Flip panel
        flipButtons: this.container.querySelectorAll('.image-editor__flip-btn'),
        
        // Adjust panel
        brightnessSlider: this.container.querySelector('#brightness-slider'),
        brightnessValue: this.container.querySelector('#brightness-value'),
        contrastSlider: this.container.querySelector('#contrast-slider'),
        contrastValue: this.container.querySelector('#contrast-value'),
        saturationSlider: this.container.querySelector('#saturation-slider'),
        saturationValue: this.container.querySelector('#saturation-value'),
        resetAdjustments: this.container.querySelector('#reset-adjustments'),
        
        // Presets panel
        presetButtons: this.container.querySelectorAll('.image-editor__preset'),
        
        // Auto panel
        autoEnhanceBtn: this.container.querySelector('#auto-enhance-btn'),
        
        // Dialog
        confirmDialog: this.container.querySelector('#confirm-dialog'),
        dialogOriginal: this.container.querySelector('#dialog-original'),
        dialogEdited: this.container.querySelector('#dialog-edited'),
        dialogChangesList: this.container.querySelector('#dialog-changes-list'),
        dialogWarning: this.container.querySelector('#dialog-warning'),
        confirmCheckbox: this.container.querySelector('#confirm-checkbox'),
        dialogBackBtn: this.container.querySelector('#dialog-back-btn'),
        dialogConfirmBtn: this.container.querySelector('#dialog-confirm-btn'),
        
        // Processing overlay
        processingOverlay: this.container.querySelector('#processing-overlay'),
        processingText: this.container.querySelector('#processing-text')
      };
    }

    parseUrlParams() {
      const params = new URLSearchParams(window.location.search);
      
      this.state.imageUrl = params.get('image') ? decodeURIComponent(params.get('image')) : null;
      this.state.productHandle = params.get('product') || null;
      this.state.slot = parseInt(params.get('slot')) || 1;
      this.state.returnUrl = params.get('return') ? decodeURIComponent(params.get('return')) : null;
      this.state.cartLineKey = params.get('cartLineKey') || null;
      
      // Parse existing edit data to restore state
      const editDataParam = params.get('editData');
      if (editDataParam) {
        try {
          this.state.existingEdits = JSON.parse(decodeURIComponent(editDataParam));
          console.log('[ImageEditor] Parsed existing edit data:', this.state.existingEdits);
        } catch (e) {
          console.error('[ImageEditor] Failed to parse edit data:', e);
          this.state.existingEdits = null;
        }
      }
      
      // If editing from cart, we're editing an existing item
      this.state.isCartEdit = !!this.state.cartLineKey;
      
      // Parse requirements from URL
      const requirementsParam = params.get('requirements');
      console.log('[ImageEditor] Raw requirements param:', requirementsParam);
      
      if (requirementsParam) {
        try {
          this.state.requirements = JSON.parse(decodeURIComponent(requirementsParam));
          console.log('[ImageEditor] Parsed requirements from URL:', this.state.requirements);
        } catch (e) {
          console.error('[ImageEditor] Failed to parse requirements from URL:', e);
          console.error('[ImageEditor] Raw value was:', requirementsParam);
        }
      } else {
        console.log('[ImageEditor] No requirements param in URL');
      }
      
      // Fallback 1: Try to get from existingEdits (stored when editing)
      if (!this.state.requirements && this.state.existingEdits?.requirements) {
        this.state.requirements = this.state.existingEdits.requirements;
        console.log('[ImageEditor] Got requirements from existingEdits:', this.state.requirements);
      }
      
      // Fallback 2: Try to get from window.imageRequirements (set by Liquid snippet)
      if (!this.state.requirements && window.imageRequirements?.hasRequirements) {
        const slotData = window.imageRequirements.slots?.find(s => s.slot === this.state.slot);
        if (slotData) {
          this.state.requirements = slotData;
          console.log('[ImageEditor] Got requirements from window.imageRequirements:', slotData);
        }
      }
      
      // Update subtitle
      if (this.state.productHandle) {
        this.elements.subtitle.textContent = `Editing for ${this.state.productHandle}`;
      }
      
      console.log('[ImageEditor] Final parsed state:', {
        imageUrl: this.state.imageUrl?.substring(0, 50) + '...',
        productHandle: this.state.productHandle,
        slot: this.state.slot,
        hasRequirements: !!this.state.requirements,
        requirements: this.state.requirements,
        isCartEdit: this.state.isCartEdit,
        cartLineKey: this.state.cartLineKey
      });
    }

    registerTools() {
      // Crop & Rotate tool (combined iPhone-style)
      this.registry.register('crop', {
        name: 'Crop & Rotate',
        category: 'transform',
        shortcut: 'c',
        panel: 'panel-crop',
        onActivate: () => this.initCropTool(true),
        onDeactivate: () => this.setCropInteractive(false)
      });

      // Flip tool
      this.registry.register('flip', {
        name: 'Flip',
        category: 'transform',
        shortcut: 'f',
        panel: 'panel-flip'
      });

      // Adjust tool
      this.registry.register('adjust', {
        name: 'Adjustments',
        category: 'adjust',
        shortcut: 'a',
        panel: 'panel-adjust'
      });

      // Presets tool
      this.registry.register('presets', {
        name: 'Presets',
        category: 'adjust',
        shortcut: 'p',
        panel: 'panel-presets'
      });

      // Auto enhance tool
      this.registry.register('auto', {
        name: 'Auto Enhance',
        category: 'adjust',
        shortcut: 'e',
        panel: 'panel-auto'
      });
    }

    bindEvents() {
      // Header buttons
      this.elements.backBtn?.addEventListener('click', () => this.goBack());
      this.elements.resetBtn?.addEventListener('click', () => this.resetAll());
      this.elements.applyBtn?.addEventListener('click', () => this.showConfirmDialog());
      
      // Tool buttons
      this.elements.toolButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          const tool = btn.dataset.tool;
          if (tool) this.setActiveTool(tool);
        });
      });
      
      // Keyboard shortcuts
      document.addEventListener('keydown', (e) => this.handleKeydown(e));
      
      // Rotate buttons
      this.elements.rotateButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          const angle = parseInt(btn.dataset.rotate);
          this.rotate90(angle);
        });
      });
      
      // Rotation slider
      this.elements.rotationSlider?.addEventListener('input', (e) => {
        this.setFreeRotation(parseInt(e.target.value));
      });
      
      // Flip buttons
      this.elements.flipButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          const direction = btn.dataset.flip;
          this.toggleFlip(direction);
          btn.classList.toggle('active');
        });
      });
      
      // Adjustment sliders
      this.elements.brightnessSlider?.addEventListener('input', (e) => {
        this.setAdjustment('brightness', parseInt(e.target.value));
      });
      this.elements.contrastSlider?.addEventListener('input', (e) => {
        this.setAdjustment('contrast', parseInt(e.target.value));
      });
      this.elements.saturationSlider?.addEventListener('input', (e) => {
        this.setAdjustment('saturation', parseInt(e.target.value));
      });
      this.elements.resetAdjustments?.addEventListener('click', () => {
        this.resetAdjustments();
      });
      
      // Presets
      this.elements.presetButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          this.setPreset(btn.dataset.preset);
        });
      });
      
      // Auto enhance
      this.elements.autoEnhanceBtn?.addEventListener('click', () => {
        this.autoEnhance();
      });
      
      // Reset to defaults (in auto panel)
      const resetDefaultsBtn = this.container.querySelector('#reset-defaults-btn');
      resetDefaultsBtn?.addEventListener('click', () => {
        this.resetAdjustments();
      });
      
      // Show grid checkbox
      this.elements.showGrid?.addEventListener('change', () => {
        this.drawCropOverlay();
      });
      
      // Rotate crop orientation button
      const rotateCropBtn = this.container.querySelector('#rotate-crop-btn');
      rotateCropBtn?.addEventListener('click', () => {
        this.rotateCropOrientation();
      });
      
      // Dialog events
      this.elements.confirmCheckbox?.addEventListener('change', (e) => {
        this.elements.dialogConfirmBtn.disabled = !e.target.checked;
      });
      this.elements.dialogBackBtn?.addEventListener('click', () => {
        this.hideConfirmDialog();
      });
      this.elements.dialogConfirmBtn?.addEventListener('click', () => {
        this.processAndSave();
      });
      
      // Window resize
      window.addEventListener('resize', () => {
        this.state.isMobile = window.innerWidth <= 768;
        if (this.state.imageUrl) {
          this.updateImageDisplay();
        }
      });
      
      // Mobile panel toggle
      if (this.state.isMobile) {
        this.elements.panel?.addEventListener('click', (e) => {
          if (e.target === this.elements.panel) {
            this.togglePanel();
          }
        });
      }
      
      // Mobile panel close button
      const panelCloseBtn = this.container.querySelector('#panel-close-btn');
      if (panelCloseBtn) {
        panelCloseBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          this.closePanel();
        });
      }
    }
    
    closePanel() {
      this.elements.panel?.classList.remove('open');
      this.state.panelOpen = false;
    }

    handleKeydown(e) {
      // Don't trigger shortcuts when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      const key = e.key.toLowerCase();
      
      // Tool shortcuts
      this.registry.getAll().forEach(tool => {
        if (tool.shortcut === key) {
          e.preventDefault();
          this.setActiveTool(tool.id);
        }
      });
      
      // Escape to close dialogs
      if (key === 'escape') {
        if (this.elements.confirmDialog?.style.display !== 'none') {
          this.hideConfirmDialog();
        }
      }
    }

    setActiveTool(toolId) {
      const tool = this.registry.get(toolId);
      if (!tool) return;
      
      // Deactivate previous tool
      const prevTool = this.registry.get(this.state.activeTool);
      if (prevTool?.onDeactivate) {
        prevTool.onDeactivate();
      }
      
      this.state.activeTool = toolId;
      
      // Update toolbar UI
      this.elements.toolButtons.forEach(btn => {
        btn.classList.toggle('image-editor__tool--active', btn.dataset.tool === toolId);
      });
      
      // Show correct panel
      this.container.querySelectorAll('.image-editor__panel-content').forEach(panel => {
        panel.style.display = 'none';
      });
      const panelEl = this.container.querySelector(`#${tool.panel}`);
      if (panelEl) {
        panelEl.style.display = 'block';
      }
      
      // On mobile, open panel
      if (this.state.isMobile) {
        this.elements.panel?.classList.add('open');
        this.state.panelOpen = true;
      }
      
      // Activate new tool
      if (tool.onActivate) {
        tool.onActivate();
      }
      
      console.log('[ImageEditor] Active tool:', toolId);
    }

    togglePanel() {
      this.state.panelOpen = !this.state.panelOpen;
      this.elements.panel?.classList.toggle('open', this.state.panelOpen);
    }

    // ========================================================================
    // IMAGE LOADING
    // ========================================================================
    async loadImage(url) {
      console.log('[ImageEditor] Loading image:', url.substring(0, 50) + '...');
      
      this.elements.loading.style.display = 'flex';
      this.elements.image.style.display = 'none';
      
      try {
        this.elements.image.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          this.elements.image.onload = resolve;
          this.elements.image.onerror = reject;
          this.elements.image.src = url;
        });
        
        this.state.originalWidth = this.elements.image.naturalWidth;
        this.state.originalHeight = this.elements.image.naturalHeight;
        
        console.log('[ImageEditor] Image loaded:', this.state.originalWidth, 'x', this.state.originalHeight);
        
        // Initialize crop to full image
        this.state.crop = {
          x: 0,
          y: 0,
          width: this.state.originalWidth,
          height: this.state.originalHeight
        };
        
        this.elements.loading.style.display = 'none';
        this.elements.image.style.display = 'block';
        
        this.updateImageDisplay();
        this.validateResolution();
        
        // Restore edit state - check multiple sources in priority order:
        // 1. URL editData (from cart - already saved edits)
        // 2. Pending edits from sessionStorage (user clicked back without applying)
        if (this.state.existingEdits) {
          console.log('[ImageEditor] Restoring from URL editData parameter');
          this.restoreEditState(this.state.existingEdits);
          // Clear pending edits since we have authoritative saved data
          this.clearPendingEdits();
        } else {
          // Check for pending edits (user clicked back without applying)
          const pendingEdits = this.loadPendingEdits();
          if (pendingEdits) {
            console.log('[ImageEditor] Restoring from pending edits (back without apply)');
            this.restoreEditState(pendingEdits);
            // Keep pending edits in sessionStorage until user applies or resets
          }
        }
        
        // Initialize crop overlay (always visible, but only interactive when crop tool is active)
        this.initCropTool(this.state.activeTool === 'crop');
        
      } catch (error) {
        console.error('[ImageEditor] Failed to load image:', error);
        this.elements.loading.innerHTML = `
          <span style="color: var(--editor-error);">Failed to load image</span>
        `;
      }
    }

    updateImageDisplay() {
      const rect = this.elements.image.getBoundingClientRect();
      this.state.displayWidth = rect.width;
      this.state.displayHeight = rect.height;
      
      // Update info bar
      this.elements.infoDimensions.textContent = 
        `${this.state.originalWidth} × ${this.state.originalHeight} px`;
      
      // DEBUG: Show requirements status
      if (this.state.requirements) {
        const req = this.state.requirements;
        // Show full requirements info
        this.elements.infoRequirements.textContent = 
          `Required: ${req.widthInches}" × ${req.heightInches}" @ ${req.dpi} DPI (min ${req.minPixelWidth}×${req.minPixelHeight}px)`;
        console.log('[ImageEditor] Requirements detected:', req);
      } else {
        // Show debug info about why requirements are missing
        this.elements.infoRequirements.textContent = 'No requirements (check URL params)';
        console.log('[ImageEditor] No requirements in state. URL params:', window.location.search);
        console.log('[ImageEditor] window.imageRequirements:', window.imageRequirements);
      }
      
      // Update crop overlay size
      this.drawCropOverlay();
    }

    validateResolution() {
      if (!this.state.requirements) {
        // No requirements - just check basic megapixels
        const megapixels = (this.state.originalWidth * this.state.originalHeight) / 1000000;
        this.state.meetsResolution = megapixels >= 1.5;
        this.state.isLowRes = !this.state.meetsResolution;
        
        this.updateValidationUI();
        return;
      }
      
      const req = this.state.requirements;
      const meetsWidth = this.state.originalWidth >= req.minPixelWidth;
      const meetsHeight = this.state.originalHeight >= req.minPixelHeight;
      
      this.state.meetsResolution = meetsWidth && meetsHeight;
      this.state.isLowRes = !this.state.meetsResolution;
      
      this.updateValidationUI();
    }

    updateValidationUI() {
      const statusEl = this.elements.infoStatus;
      const statusText = this.elements.infoStatusText;
      const warning = this.elements.resolutionWarning;
      
      if (this.state.meetsResolution) {
        statusEl.className = 'image-editor__info-status valid';
        statusText.textContent = 'Resolution OK';
        warning.style.display = 'none';
      } else {
        statusEl.className = 'image-editor__info-status warning';
        statusText.textContent = 'Low Resolution';
        warning.style.display = 'flex';
        
        if (this.state.requirements) {
          const req = this.state.requirements;
          this.elements.warningText.textContent = 
            `Image is ${this.state.originalWidth}×${this.state.originalHeight}px. ` +
            `Recommended: ${req.minPixelWidth}×${req.minPixelHeight}px minimum. ` +
            `AI upscaling will be applied.`;
        } else {
          this.elements.warningText.textContent = 
            'Image resolution is below 1.5 megapixels. Quality may be affected.';
        }
      }
    }

    // ========================================================================
    // CROP TOOL
    // ========================================================================
    initCropTool(interactive = false) {
      if (!this.elements.image.complete) return;
      
      // Calculate aspect ratio from requirements or use image's natural ratio
      let aspectRatio;
      if (this.state.requirements) {
        aspectRatio = this.state.requirements.aspectRatio;
        this.elements.aspectDisplay.textContent = 
          `${this.state.requirements.widthInches}:${this.state.requirements.heightInches}`;
      } else {
        aspectRatio = this.state.originalWidth / this.state.originalHeight;
        const gcd = this.gcd(this.state.originalWidth, this.state.originalHeight);
        this.elements.aspectDisplay.textContent = 
          `${this.state.originalWidth/gcd}:${this.state.originalHeight/gcd}`;
      }
      
      // Store the required aspect ratio for later use
      this.state.requiredAspectRatio = aspectRatio;
      
      // Initialize crop area ONLY if not already initialized
      // This preserves user's crop selection when switching between tools
      if (!this.state.cropInitialized) {
        this.calculateInitialCrop(aspectRatio);
        this.state.cropInitialized = true;
        console.log('[ImageEditor] Crop initialized with aspect ratio:', aspectRatio);
      } else {
        console.log('[ImageEditor] Crop already initialized, preserving user selection');
      }
      
      // Draw overlay (always visible)
      this.drawCropOverlay();
      
      // Update status bar with crop dimensions
      this.updateCropStatus();
      
      // Make overlay interactive only if crop tool is active
      if (interactive) {
        this.initCropInteraction();
        this.setCropInteractive(true);
      }
      
      // Update rotate crop button visibility
      this.updateCropRotateButtonVisibility();
    }

    setCropInteractive(interactive) {
      const canvas = this.elements.cropOverlay;
      if (interactive) {
        canvas.classList.add('interactive');
        canvas.style.cursor = 'move';
      } else {
        canvas.classList.remove('interactive');
        canvas.style.cursor = 'default';
      }
    }

    /**
     * Rotate the crop orientation by swapping width and height
     * This allows 4x6 to become 6x4 (landscape <-> portrait)
     */
    rotateCropOrientation() {
      if (!this.state.requirements) {
        console.log('[ImageEditor] No requirements - cannot rotate crop orientation');
        return;
      }
      
      // Swap width and height in requirements
      const oldWidth = this.state.requirements.widthInches;
      const oldHeight = this.state.requirements.heightInches;
      
      this.state.requirements.widthInches = oldHeight;
      this.state.requirements.heightInches = oldWidth;
      
      // Swap min pixel dimensions too
      const oldMinW = this.state.requirements.minPixelWidth;
      const oldMinH = this.state.requirements.minPixelHeight;
      
      this.state.requirements.minPixelWidth = oldMinH;
      this.state.requirements.minPixelHeight = oldMinW;
      
      // Recalculate aspect ratio
      this.state.requirements.aspectRatio = this.state.requirements.widthInches / this.state.requirements.heightInches;
      this.state.requiredAspectRatio = this.state.requirements.aspectRatio;
      
      // Track that orientation was rotated (for persistence)
      this.state.cropOrientationRotated = !this.state.cropOrientationRotated;
      
      console.log('[ImageEditor] Crop orientation rotated:', {
        from: `${oldWidth}" x ${oldHeight}"`,
        to: `${this.state.requirements.widthInches}" x ${this.state.requirements.heightInches}"`,
        newAspectRatio: this.state.requirements.aspectRatio
      });
      
      // Update aspect ratio display
      this.elements.aspectDisplay.textContent = 
        `${this.state.requirements.widthInches}:${this.state.requirements.heightInches}`;
      
      // Recalculate crop with new aspect ratio (force reinit)
      this.state.cropInitialized = false;
      this.calculateInitialCrop(this.state.requirements.aspectRatio);
      this.state.cropInitialized = true;
      
      // Redraw overlay
      this.drawCropOverlay();
      this.updateCropStatus();
    }

    /**
     * Check if crop rotation is allowed and update UI accordingly
     */
    updateCropRotateButtonVisibility() {
      const rotateBtn = this.container.querySelector('#rotate-crop-btn');
      if (!rotateBtn) return;
      
      // Show button only if:
      // 1. We have requirements (fixed aspect ratio)
      // 2. allowCropRotate is true in requirements
      const allowRotate = this.state.requirements?.allowCropRotate === true;
      
      rotateBtn.style.display = allowRotate ? 'flex' : 'none';
      
      console.log('[ImageEditor] Crop rotate button visibility:', allowRotate);
    }

    updateCropStatus() {
      // Update info bar with both original and cropped dimensions
      const cropW = Math.round(this.state.crop.width);
      const cropH = Math.round(this.state.crop.height);
      
      // Check if free rotation will cause corners
      const hasFreeRotation = this.state.rotation !== 0 && 
        this.state.rotation !== 90 && 
        this.state.rotation !== 180 && 
        this.state.rotation !== 270;
      
      const rotationNote = hasFreeRotation ? ` | Rotation: ${this.state.rotation}°` : '';
      
      // Show original and cropped dimensions
      this.elements.infoDimensions.textContent = 
        `Original: ${this.state.originalWidth} × ${this.state.originalHeight} px | Crop: ${cropW} × ${cropH} px${rotationNote}`;
      
      // Calculate if cropped dimensions meet requirements
      if (this.state.requirements) {
        const req = this.state.requirements;
        const meetsWidth = cropW >= req.minPixelWidth;
        const meetsHeight = cropH >= req.minPixelHeight;
        const meetsCrop = meetsWidth && meetsHeight;
        
        // Update status
        const statusEl = this.elements.infoStatus;
        const statusText = this.elements.infoStatusText;
        
        if (meetsCrop && !hasFreeRotation) {
          statusEl.className = 'image-editor__info-status valid';
          statusText.textContent = 'Crop meets requirements';
          this.elements.resolutionWarning.style.display = 'none';
        } else if (meetsCrop && hasFreeRotation) {
          statusEl.className = 'image-editor__info-status warning';
          statusText.textContent = 'Free rotation adds corners';
          this.elements.resolutionWarning.style.display = 'flex';
          this.elements.warningText.textContent = 
            `Rotation at ${this.state.rotation}° will add white corners. Use 90° increments for edge-to-edge coverage.`;
        } else {
          statusEl.className = 'image-editor__info-status warning';
          statusText.textContent = `Crop too small (need ${req.minPixelWidth}×${req.minPixelHeight})`;
          this.elements.resolutionWarning.style.display = 'flex';
          this.elements.warningText.textContent = 
            `Cropped area is ${cropW}×${cropH}px. Minimum required: ${req.minPixelWidth}×${req.minPixelHeight}px for ${req.dpi} DPI printing.`;
        }
      }
    }

    calculateInitialCrop(aspectRatio) {
      const imgW = this.state.originalWidth;
      const imgH = this.state.originalHeight;
      const imgAspect = imgW / imgH;
      
      let cropW, cropH;
      
      if (aspectRatio > imgAspect) {
        // Crop is wider - fit to width
        cropW = imgW;
        cropH = imgW / aspectRatio;
      } else {
        // Crop is taller - fit to height
        cropH = imgH;
        cropW = imgH * aspectRatio;
      }
      
      // Center the crop
      this.state.crop = {
        x: (imgW - cropW) / 2,
        y: (imgH - cropH) / 2,
        width: cropW,
        height: cropH
      };
      
      console.log('[ImageEditor] Crop calculated:', {
        aspectRatio,
        imgDimensions: `${imgW}x${imgH}`,
        cropDimensions: `${Math.round(cropW)}x${Math.round(cropH)}`,
        requirements: this.state.requirements ? 
          `${this.state.requirements.widthInches}"x${this.state.requirements.heightInches}" @ ${this.state.requirements.dpi}DPI` : 
          'none'
      });
    }

    drawCropOverlay() {
      const canvas = this.elements.cropOverlay;
      const ctx = canvas.getContext('2d');
      const img = this.elements.image;
      
      if (!img.complete || !img.naturalWidth) return;
      
      const wrapperRect = this.elements.canvasWrapper.getBoundingClientRect();
      
      // Set canvas size to match wrapper
      canvas.width = wrapperRect.width;
      canvas.height = wrapperRect.height;
      canvas.style.width = wrapperRect.width + 'px';
      canvas.style.height = wrapperRect.height + 'px';
      
      const rotation = this.state.rotation || 0;
      
      // Calculate base rotation the SAME way as server:
      // Server uses: const baseRotation = Math.round(edits.rotation / 90) * 90;
      const baseRotation = Math.round(rotation / 90) * 90;
      const normalizedBase = ((baseRotation % 360) + 360) % 360;
      const isBaseRotated = (normalizedBase === 90 || normalizedBase === 270);
      const effectiveW = isBaseRotated ? this.state.originalHeight : this.state.originalWidth;
      const effectiveH = isBaseRotated ? this.state.originalWidth : this.state.originalHeight;
      
      // Calculate rotated bounding box - use Math.ceil to match server!
      const radians = Math.abs(rotation * Math.PI / 180);
      const cos = Math.abs(Math.cos(radians));
      const sin = Math.abs(Math.sin(radians));
      
      // Bounding box of original image after rotation (ceil matches server)
      const rotatedBoundingW = Math.ceil(this.state.originalWidth * cos + this.state.originalHeight * sin);
      const rotatedBoundingH = Math.ceil(this.state.originalWidth * sin + this.state.originalHeight * cos);
      
      // Scale to fit rotated bounding box in wrapper (matches applyTransforms)
      const scaleToFit = Math.min(wrapperRect.width / rotatedBoundingW, wrapperRect.height / rotatedBoundingH);
      
      // The displayed ROTATED bounding box size
      const rotatedDisplayW = rotatedBoundingW * scaleToFit;
      const rotatedDisplayH = rotatedBoundingH * scaleToFit;
      
      // The displayed image dimensions (before rotation) scaled to fit
      const imgDisplayW = this.state.originalWidth * scaleToFit;
      const imgDisplayH = this.state.originalHeight * scaleToFit;
      
      // The EFFECTIVE image dimensions after base rotation (what crop coords are in)
      // When base rotation is 90/270, effective dimensions are swapped
      const displayedW = isBaseRotated ? imgDisplayH : imgDisplayW;
      const displayedH = isBaseRotated ? imgDisplayW : imgDisplayH;
      
      const centerX = wrapperRect.width / 2;
      const centerY = wrapperRect.height / 2;
      
      // Both CSS rotation and server's Sharp rotation create the same rotated image
      // in an expanded bounding box. The crop overlay should be positioned at the
      // correct offset within this bounding box - NO rotation transform needed.
      
      const fineRotation = this.state.fineRotation || 0;
      const hasFreeRotation = fineRotation !== 0;
      
      // Top-left of the rotated bounding box on screen
      const rotatedLeft = centerX - rotatedDisplayW / 2;
      const rotatedTop = centerY - rotatedDisplayH / 2;
      
      // Offset from effective area origin to rotated canvas origin (in original pixels)
      const offsetXPixels = (rotatedBoundingW - effectiveW) / 2;
      const offsetYPixels = (rotatedBoundingH - effectiveH) / 2;
      
      // Position of crop in the rotated canvas (in original pixels)
      const rotatedCanvasX = this.state.crop.x + offsetXPixels;
      const rotatedCanvasY = this.state.crop.y + offsetYPixels;
      
      // Convert to screen position using the uniform scale
      const cropScreenX = rotatedLeft + rotatedCanvasX * scaleToFit;
      const cropScreenY = rotatedTop + rotatedCanvasY * scaleToFit;
      const cropScreenW = this.state.crop.width * scaleToFit;
      const cropScreenH = this.state.crop.height * scaleToFit;
      
      console.log('[ImageEditor] drawCropOverlay:', {
        rotation: this.state.rotation,
        hasFreeRotation,
        effective: { w: effectiveW, h: effectiveH },
        rotatedBounding: { w: rotatedBoundingW.toFixed(0), h: rotatedBoundingH.toFixed(0) },
        offset: { x: offsetXPixels.toFixed(1), y: offsetYPixels.toFixed(1) },
        rotatedCanvasPos: { x: rotatedCanvasX.toFixed(1), y: rotatedCanvasY.toFixed(1) },
        crop: this.state.crop,
        cropScreen: { x: cropScreenX.toFixed(1), y: cropScreenY.toFixed(1), w: cropScreenW.toFixed(1), h: cropScreenH.toFixed(1) }
      });
      
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw darkened overlay
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Clear crop area (axis-aligned, not rotated)
      ctx.clearRect(cropScreenX, cropScreenY, cropScreenW, cropScreenH);
      
      // Draw crop border
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.strokeRect(cropScreenX, cropScreenY, cropScreenW, cropScreenH);
      
      // Draw rule of thirds grid if enabled
      if (this.elements.showGrid?.checked) {
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
        ctx.lineWidth = 1;
        
        const thirdW = cropScreenW / 3;
        ctx.beginPath();
        ctx.moveTo(cropScreenX + thirdW, cropScreenY);
        ctx.lineTo(cropScreenX + thirdW, cropScreenY + cropScreenH);
        ctx.moveTo(cropScreenX + thirdW * 2, cropScreenY);
        ctx.lineTo(cropScreenX + thirdW * 2, cropScreenY + cropScreenH);
        ctx.stroke();
        
        const thirdH = cropScreenH / 3;
        ctx.beginPath();
        ctx.moveTo(cropScreenX, cropScreenY + thirdH);
        ctx.lineTo(cropScreenX + cropScreenW, cropScreenY + thirdH);
        ctx.moveTo(cropScreenX, cropScreenY + thirdH * 2);
        ctx.lineTo(cropScreenX + cropScreenW, cropScreenY + thirdH * 2);
        ctx.stroke();
      }
      
      // Draw corner handles
      const handleSize = 12;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(cropScreenX - handleSize/2, cropScreenY - handleSize/2, handleSize, handleSize);
      ctx.fillRect(cropScreenX + cropScreenW - handleSize/2, cropScreenY - handleSize/2, handleSize, handleSize);
      ctx.fillRect(cropScreenX - handleSize/2, cropScreenY + cropScreenH - handleSize/2, handleSize, handleSize);
      ctx.fillRect(cropScreenX + cropScreenW - handleSize/2, cropScreenY + cropScreenH - handleSize/2, handleSize, handleSize);
      
      // Store display coordinates for interaction
      // Scale converts screen pixels to effective/original pixels
      this._cropDisplay = { 
        x: cropScreenX, 
        y: cropScreenY, 
        w: cropScreenW, 
        h: cropScreenH, 
        scaleX: scaleToFit,  // Screen to effective pixels (1 pixel movement = 1/scaleToFit effective pixels)
        scaleY: scaleToFit,
        // Offset in original pixels (for bounds checking)
        offsetXPixels,
        offsetYPixels,
        // Wrapper center
        centerX,
        centerY,
        displayedW,
        displayedH,
        hasFreeRotation,
        rotation: this.state.rotation 
      };
      
      canvas.style.display = 'block';
    }

    initCropInteraction() {
      const canvas = this.elements.cropOverlay;
      canvas.classList.add('interactive');
      
      let isDragging = false;
      let dragType = null; // 'move', 'nw', 'ne', 'sw', 'se'
      let startX, startY;
      let startCrop;
      
      const getPosition = (e) => {
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return { x: clientX - rect.left, y: clientY - rect.top };
      };
      
      const getDragType = (x, y) => {
        if (!this._cropDisplay) return null;
        const { x: cx, y: cy, w: cw, h: ch } = this._cropDisplay;
        const handleSize = 20;
        
        // Simple axis-aligned hit testing
        if (Math.abs(x - cx) < handleSize && Math.abs(y - cy) < handleSize) return 'nw';
        if (Math.abs(x - (cx + cw)) < handleSize && Math.abs(y - cy) < handleSize) return 'ne';
        if (Math.abs(x - cx) < handleSize && Math.abs(y - (cy + ch)) < handleSize) return 'sw';
        if (Math.abs(x - (cx + cw)) < handleSize && Math.abs(y - (cy + ch)) < handleSize) return 'se';
        
        if (x > cx && x < cx + cw && y > cy && y < cy + ch) return 'move';
        
        return null;
      };
      
      const handleStart = (e) => {
        const pos = getPosition(e);
        dragType = getDragType(pos.x, pos.y);
        
        if (dragType) {
          isDragging = true;
          startX = pos.x;
          startY = pos.y;
          startCrop = { ...this.state.crop };
          e.preventDefault();
        }
      };
      
      const handleMove = (e) => {
        const pos = getPosition(e);
        
        if (!isDragging) {
          // Update cursor
          const type = getDragType(pos.x, pos.y);
          if (type === 'move') canvas.style.cursor = 'move';
          else if (type === 'nw' || type === 'se') canvas.style.cursor = 'nwse-resize';
          else if (type === 'ne' || type === 'sw') canvas.style.cursor = 'nesw-resize';
          else canvas.style.cursor = 'default';
          return;
        }
        
        // Calculate display delta and convert to effective coordinates
        // The crop overlay is positioned using linear scaling (no rotation transform)
        // so screen movement directly maps to effective coordinate movement
        const displayDx = pos.x - startX;
        const displayDy = pos.y - startY;
        
        const dx = displayDx / this._cropDisplay.scaleX;
        const dy = displayDy / this._cropDisplay.scaleY;
        
        const aspectRatio = this.state.requiredAspectRatio || 
          this.state.requirements?.aspectRatio || 
          (this.state.originalWidth / this.state.originalHeight);
        
        // Get dimensions for bounds - use rotated canvas (includes white corners for free rotation)
        const effective = this.getEffectiveImageDimensions();
        const rotated = this.getRotatedCanvasDimensions();
        
        // Calculate offset from effective space to rotated canvas
        const offsetX = (rotated.width - effective.width) / 2;
        const offsetY = (rotated.height - effective.height) / 2;
        
        // Bounds in effective space (can go negative into white corners)
        const minX = -offsetX;
        const minY = -offsetY;
        const maxX = effective.width + offsetX;
        const maxY = effective.height + offsetY;
        
        if (dragType === 'move') {
          // Move crop area
          let newX = startCrop.x + dx;
          let newY = startCrop.y + dy;
          
          // Constrain to rotated canvas bounds
          newX = Math.max(minX, Math.min(newX, maxX - startCrop.width));
          newY = Math.max(minY, Math.min(newY, maxY - startCrop.height));
          
          this.state.crop.x = newX;
          this.state.crop.y = newY;
        } else {
          // Resize from corner - maintain aspect ratio
          let newW = startCrop.width;
          let newH = startCrop.height;
          let newX = startCrop.x;
          let newY = startCrop.y;
          
          if (dragType === 'se') {
            newW = startCrop.width + dx;
            newH = newW / aspectRatio;
          } else if (dragType === 'nw') {
            newW = startCrop.width - dx;
            newH = newW / aspectRatio;
            newX = startCrop.x + dx;
            newY = startCrop.y + (startCrop.height - newH);
          } else if (dragType === 'ne') {
            newW = startCrop.width + dx;
            newH = newW / aspectRatio;
            newY = startCrop.y + (startCrop.height - newH);
          } else if (dragType === 'sw') {
            newW = startCrop.width - dx;
            newH = newW / aspectRatio;
            newX = startCrop.x + dx;
          }
          
          // Minimum size
          const minSize = 50;
          if (newW >= minSize && newH >= minSize) {
            // Constrain to rotated canvas bounds
            if (newX < minX) {
              newW += (newX - minX);
              newH = newW / aspectRatio;
              newX = minX;
            }
            if (newY < minY) {
              newH += (newY - minY);
              newW = newH * aspectRatio;
              newY = minY;
            }
            if (newX + newW > maxX) {
              newW = maxX - newX;
              newH = newW / aspectRatio;
            }
            if (newY + newH > maxY) {
              newH = maxY - newY;
              newW = newH * aspectRatio;
            }
            
            this.state.crop = { x: newX, y: newY, width: newW, height: newH };
          }
        }
        
        this.drawCropOverlay();
        this.updateCropStatus();
        e.preventDefault();
      };
      
      const handleEnd = () => {
        isDragging = false;
        dragType = null;
      };
      
      // Mouse events
      canvas.addEventListener('mousedown', handleStart);
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      
      // Touch events
      canvas.addEventListener('touchstart', handleStart, { passive: false });
      document.addEventListener('touchmove', handleMove, { passive: false });
      document.addEventListener('touchend', handleEnd);
    }

    hideCropOverlay() {
      // Don't hide the overlay, just remove interactivity
      // The overlay should always be visible to show product boundaries
      this.setCropInteractive(false);
    }

    // ========================================================================
    // ROTATION SAFE BOUNDS CALCULATION
    // ========================================================================
    
    // Get the effective image dimensions based on rotation
    // (Used for coordinate system - crop coords are relative to this)
    // MUST match server's calculation: const baseRotation = Math.round(edits.rotation / 90) * 90;
    getEffectiveImageDimensions() {
      const rotation = this.state.rotation || 0;
      const baseRotation = Math.round(rotation / 90) * 90;
      const normalizedBase = ((baseRotation % 360) + 360) % 360;
      const isRotated = (normalizedBase === 90 || normalizedBase === 270);
      return {
        width: isRotated ? this.state.originalHeight : this.state.originalWidth,
        height: isRotated ? this.state.originalWidth : this.state.originalHeight
      };
    }
    
    // Get the full rotated canvas dimensions (including expansion from free rotation)
    // This is the actual usable area for cropping
    // MUST use Math.ceil to match server calculation!
    getRotatedCanvasDimensions() {
      const rotation = this.state.rotation || 0;
      const radians = Math.abs(rotation * Math.PI / 180);
      const cos = Math.abs(Math.cos(radians));
      const sin = Math.abs(Math.sin(radians));
      
      // Bounding box of the rotated original image (ceil matches server)
      const rotatedW = Math.ceil(this.state.originalWidth * cos + this.state.originalHeight * sin);
      const rotatedH = Math.ceil(this.state.originalWidth * sin + this.state.originalHeight * cos);
      
      return { width: rotatedW, height: rotatedH };
    }
    
    // Transform crop coordinates from effective (display) space to original image space
    // This is needed because server processes: crop original → rotate → flip
    // So we need to provide crop coords in original image space
    //
    // At 90° CW rotation:  original (ox, oy) → display (origH - oy, ox)
    // At 180° rotation:    original (ox, oy) → display (origW - ox, origH - oy)  
    // At 270° CW rotation: original (ox, oy) → display (oy, origW - ox)
    //
    // We need the INVERSE to go from display (effective) back to original
    transformCropToOriginalSpace(effectiveCrop, rotation, origW, origH) {
      const { x, y, width, height } = effectiveCrop;
      
      console.log('[ImageEditor] transformCropToOriginalSpace input:', {
        effectiveCrop, rotation, origW, origH
      });
      
      let result;
      switch (rotation % 360) {
        case 0:
          // No rotation - coords are already in original space
          result = { x, y, width, height };
          break;
          
        case 90:
          // 90° CW: display is H×W (2160×3840 for 3840×2160 original)
          // Inverse: display (dx, dy) → original (dy, origH - dx - dw)
          // Width/height swap: display width → original height, display height → original width
          result = {
            x: y,
            y: origH - x - width,
            width: height,
            height: width
          };
          break;
          
        case 180:
          // 180°: same dimensions, just inverted
          // Inverse: display (dx, dy) → original (origW - dx - dw, origH - dy - dh)
          result = {
            x: origW - x - width,
            y: origH - y - height,
            width,
            height
          };
          break;
          
        case 270:
          // 270° CW: display is H×W (2160×3840 for 3840×2160 original)
          // Inverse: display (dx, dy) → original (origW - dy - dh, dx)
          // Width/height swap: display width → original height, display height → original width
          result = {
            x: origW - y - height,
            y: x,
            width: height,
            height: width
          };
          break;
          
        default:
          console.warn('[ImageEditor] Unexpected rotation:', rotation);
          result = { x, y, width, height };
      }
      
      console.log('[ImageEditor] transformCropToOriginalSpace output:', result);
      return result;
    }
    
    // Inverse transformation: from original image space back to effective (display) space
    // Used when restoring saved edits
    transformCropToEffectiveSpace(originalCrop, rotation, origW, origH) {
      const { x, y, width, height } = originalCrop;
      
      console.log('[ImageEditor] transformCropToEffectiveSpace input:', {
        originalCrop, rotation, origW, origH
      });
      
      let result;
      switch (rotation % 360) {
        case 0:
          // No rotation - coords are already correct
          result = { x, y, width, height };
          break;
          
        case 90:
          // Inverse of 90° transform
          // Original to effective: swap dimensions, transform coords
          result = {
            x: origH - y - height,
            y: x,
            width: height,
            height: width
          };
          break;
          
        case 180:
          // Inverse of 180° transform
          result = {
            x: origW - x - width,
            y: origH - y - height,
            width,
            height
          };
          break;
          
        case 270:
          // Inverse of 270° transform
          result = {
            x: y,
            y: origW - x - width,
            width: height,
            height: width
          };
          break;
          
        default:
          console.warn('[ImageEditor] Unexpected rotation:', rotation);
          result = { x, y, width, height };
      }
      
      console.log('[ImageEditor] transformCropToEffectiveSpace output:', result);
      return result;
    }
    
    // Get safe bounds for crop - returns full rotated canvas area
    // For free rotation, users can crop into the white corner areas
    getRotatedSafeBounds() {
      const effective = this.getEffectiveImageDimensions();
      const rotated = this.getRotatedCanvasDimensions();
      const fineRotation = this.state.fineRotation || 0;
      
      // Calculate offset (negative x/y means into white corners)
      const offsetX = (rotated.width - effective.width) / 2;
      const offsetY = (rotated.height - effective.height) / 2;
      
      // Return bounds in effective coordinate space
      return { 
        x: -offsetX, 
        y: -offsetY, 
        width: rotated.width, 
        height: rotated.height, 
        hasCornersRisk: fineRotation !== 0 
      };
    }
    
    // Light constraint - ensure crop stays within the rotated canvas bounds
    constrainCropToRotation() {
      // Use full rotated canvas dimensions (including white corners for free rotation)
      const dims = this.getRotatedCanvasDimensions();
      const effective = this.getEffectiveImageDimensions();
      const crop = this.state.crop;
      
      // Calculate offset from effective to rotated canvas
      const offsetX = (dims.width - effective.width) / 2;
      const offsetY = (dims.height - effective.height) / 2;
      
      // Crop coords are in effective space, so bounds are:
      // min: -offsetX, -offsetY (can go into white corners)
      // max: effective + offset on other side
      const minX = -offsetX;
      const minY = -offsetY;
      const maxX = effective.width + offsetX - crop.width;
      const maxY = effective.height + offsetY - crop.height;
      
      if (crop.x < minX) crop.x = minX;
      if (crop.y < minY) crop.y = minY;
      if (crop.x > maxX) crop.x = Math.max(minX, maxX);
      if (crop.y > maxY) crop.y = Math.max(minY, maxY);
    }

    // ========================================================================
    // ROTATE TOOL
    // ========================================================================
    rotate90(angle) {
      // 90° rotation button - adds/subtracts 90 from base rotation
      // Fine rotation resets to 0 when using 90° buttons
      let newBase = (this.state.baseRotation + angle) % 360;
      if (newBase < 0) newBase += 360;
      
      this.state.baseRotation = newBase;
      this.state.fineRotation = 0;
      this.state.rotation = newBase;
      
      // Update displays
      if (this.elements.rotationSlider) {
        this.elements.rotationSlider.value = 0;
      }
      if (this.elements.rotationValue) {
        this.elements.rotationValue.textContent = newBase + '°';
      }
      
      console.log('[ImageEditor] 90° rotation:', { base: newBase, fine: 0, total: newBase });
      
      // Recalculate crop for the new orientation
      this.recalculateCropForRotation();
      this.applyTransforms();
      this.drawCropOverlay();
      this.updateCropStatus();
    }

    setFreeRotation(angle) {
      const fineAngle = parseInt(angle);
      const baseRotation = this.state.baseRotation || 0;
      
      this.state.fineRotation = fineAngle;
      this.state.rotation = baseRotation + fineAngle;
      
      // Update display - show total rotation
      if (this.elements.rotationValue) {
        this.elements.rotationValue.textContent = this.state.rotation + '°';
      }
      
      console.log('[ImageEditor] Free rotation:', { base: baseRotation, fine: fineAngle, total: this.state.rotation });
      
      // Constrain crop to stay within visible bounds when rotated
      this.constrainCropToRotation();
      this.applyTransforms();
      this.drawCropOverlay();
      this.updateCropStatus();
    }
    
    recalculateCropForRotation() {
      // After a 90° rotation, recalculate crop to fit within new bounds
      // while maintaining the required aspect ratio
      const aspectRatio = this.state.requiredAspectRatio || (this.state.crop.width / this.state.crop.height);
      
      // Get effective dimensions using server-consistent formula
      const rotation = this.state.rotation || 0;
      const baseRotation = Math.round(rotation / 90) * 90;
      const normalizedBase = ((baseRotation % 360) + 360) % 360;
      const isRotated90or270 = (normalizedBase === 90 || normalizedBase === 270);
      
      const imgW = isRotated90or270 ? this.state.originalHeight : this.state.originalWidth;
      const imgH = isRotated90or270 ? this.state.originalWidth : this.state.originalHeight;
      
      // Calculate the maximum crop that fits within the rotated image
      let cropW, cropH;
      const imgAspect = imgW / imgH;
      
      if (aspectRatio > imgAspect) {
        // Crop is wider - fit to width
        cropW = imgW;
        cropH = imgW / aspectRatio;
      } else {
        // Crop is taller - fit to height
        cropH = imgH;
        cropW = imgH * aspectRatio;
      }
      
      // Center the crop
      this.state.crop = {
        x: (imgW - cropW) / 2,
        y: (imgH - cropH) / 2,
        width: cropW,
        height: cropH
      };
      
      // Mark that crop was recalculated
      this.state.cropInitialized = true;
      
      console.log('[ImageEditor] Crop recalculated for rotation:', {
        baseRotation: normalizedBase,
        effectiveImageSize: imgW + 'x' + imgH,
        crop: this.state.crop
      });
    }

    // ========================================================================
    // FLIP TOOL
    // ========================================================================
    toggleFlip(direction) {
      if (direction === 'horizontal') {
        this.state.flip.horizontal = !this.state.flip.horizontal;
      } else if (direction === 'vertical') {
        this.state.flip.vertical = !this.state.flip.vertical;
      }
      
      console.log('[ImageEditor] Flip:', this.state.flip);
      this.applyTransforms();
    }

    // ========================================================================
    // ADJUSTMENTS
    // ========================================================================
    setAdjustment(type, value) {
      this.state.adjustments[type] = value;
      
      // Update display value
      if (type === 'brightness') this.elements.brightnessValue.textContent = value;
      if (type === 'contrast') this.elements.contrastValue.textContent = value;
      if (type === 'saturation') this.elements.saturationValue.textContent = value;
      
      // Reset preset to original when manual adjustment is made
      this.state.preset = 'original';
      this.updatePresetUI();
      
      this.applyFilters();
    }

    resetAdjustments() {
      this.state.adjustments = { brightness: 0, contrast: 0, saturation: 0 };
      
      this.elements.brightnessSlider.value = 0;
      this.elements.brightnessValue.textContent = '0';
      this.elements.contrastSlider.value = 0;
      this.elements.contrastValue.textContent = '0';
      this.elements.saturationSlider.value = 0;
      this.elements.saturationValue.textContent = '0';
      
      this.state.preset = 'original';
      this.updatePresetUI();
      
      this.applyFilters();
    }

    // ========================================================================
    // PRESETS
    // ========================================================================
    setPreset(presetName) {
      this.state.preset = presetName;
      
      // Preset adjustment values
      const presets = {
        original: { brightness: 0, contrast: 0, saturation: 0 },
        vivid: { brightness: 5, contrast: 10, saturation: 30 },
        warm: { brightness: 5, contrast: 5, saturation: 10 },
        cool: { brightness: 2, contrast: 5, saturation: 10 },
        bw: { brightness: 0, contrast: 10, saturation: -100 },
        vintage: { brightness: -5, contrast: -10, saturation: -20 }
      };
      
      const preset = presets[presetName] || presets.original;
      this.state.adjustments = { ...preset };
      
      // Update slider UI
      this.elements.brightnessSlider.value = preset.brightness;
      this.elements.brightnessValue.textContent = preset.brightness;
      this.elements.contrastSlider.value = preset.contrast;
      this.elements.contrastValue.textContent = preset.contrast;
      this.elements.saturationSlider.value = preset.saturation;
      this.elements.saturationValue.textContent = preset.saturation;
      
      this.updatePresetUI();
      this.applyFilters();
    }

    updatePresetUI() {
      this.elements.presetButtons.forEach(btn => {
        btn.classList.toggle('image-editor__preset--active', btn.dataset.preset === this.state.preset);
      });
    }

    // ========================================================================
    // AUTO ENHANCE
    // ========================================================================
    autoEnhance() {
      // Simple auto-enhance: slight brightness and contrast boost
      // In a more advanced implementation, this would analyze the histogram
      this.state.adjustments = { brightness: 10, contrast: 15, saturation: 10 };
      this.state.preset = 'original';
      
      // Update sliders
      this.elements.brightnessSlider.value = 10;
      this.elements.brightnessValue.textContent = '10';
      this.elements.contrastSlider.value = 15;
      this.elements.contrastValue.textContent = '15';
      this.elements.saturationSlider.value = 10;
      this.elements.saturationValue.textContent = '10';
      
      this.updatePresetUI();
      this.applyFilters();
      
      console.log('[ImageEditor] Auto-enhance applied');
    }

    // ========================================================================
    // APPLY TRANSFORMS AND FILTERS (Client-side preview)
    // ========================================================================
    applyTransforms() {
      const transforms = [];
      const img = this.elements.image;
      const wrapper = this.elements.canvasWrapper;
      
      if (!wrapper) {
        // No wrapper yet, just apply basic transforms
        if (this.state.rotation !== 0) {
          transforms.push(`rotate(${this.state.rotation}deg)`);
        }
        if (this.state.flip.horizontal) {
          transforms.push('scaleX(-1)');
        }
        if (this.state.flip.vertical) {
          transforms.push('scaleY(-1)');
        }
        img.style.transform = transforms.join(' ');
        return;
      }
      
      // Get wrapper dimensions
      const wrapperRect = wrapper.getBoundingClientRect();
      const wrapperW = wrapperRect.width;
      const wrapperH = wrapperRect.height;
      
      const originalW = this.state.originalWidth;
      const originalH = this.state.originalHeight;
      const rotation = this.state.rotation || 0;
      
      // Calculate the bounding box of the rotated image
      const radians = Math.abs(rotation * Math.PI / 180);
      const cos = Math.abs(Math.cos(radians));
      const sin = Math.abs(Math.sin(radians));
      
      // Bounding box dimensions after rotation
      const rotatedBoundingW = originalW * cos + originalH * sin;
      const rotatedBoundingH = originalW * sin + originalH * cos;
      
      // Scale to fit the rotated bounding box within the wrapper
      const scaleToFit = Math.min(wrapperW / rotatedBoundingW, wrapperH / rotatedBoundingH);
      
      // Set explicit size on image so it doesn't jump when rotating
      // The image itself stays at natural aspect ratio, scaled uniformly
      const imgDisplayW = originalW * scaleToFit;
      const imgDisplayH = originalH * scaleToFit;
      
      img.style.width = imgDisplayW + 'px';
      img.style.height = imgDisplayH + 'px';
      img.style.maxWidth = 'none';
      img.style.maxHeight = 'none';
      
      if (this.state.rotation !== 0) {
        transforms.push(`rotate(${this.state.rotation}deg)`);
      }
      if (this.state.flip.horizontal) {
        transforms.push('scaleX(-1)');
      }
      if (this.state.flip.vertical) {
        transforms.push('scaleY(-1)');
      }
      
      img.style.transform = transforms.join(' ');
      
      // Redraw crop overlay after transform changes
      requestAnimationFrame(() => this.drawCropOverlay());
    }

    applyFilters() {
      const { brightness, contrast, saturation } = this.state.adjustments;
      
      // Convert values to CSS filter format
      const brightnessFilter = 1 + (brightness / 100);
      const contrastFilter = 1 + (contrast / 100);
      const saturateFilter = 1 + (saturation / 100);
      
      const filters = [];
      if (brightness !== 0) filters.push(`brightness(${brightnessFilter})`);
      if (contrast !== 0) filters.push(`contrast(${contrastFilter})`);
      if (saturation !== 0) filters.push(`saturate(${saturateFilter})`);
      
      this.elements.image.style.filter = filters.join(' ');
    }

    // ========================================================================
    // RESET
    // ========================================================================
    resetAll() {
      // Reset state - MUST reset ALL rotation components
      this.state.crop = {
        x: 0,
        y: 0,
        width: this.state.originalWidth,
        height: this.state.originalHeight
      };
      this.state.cropInitialized = false; // Allow re-initialization with proper aspect ratio
      this.state.rotation = 0;
      this.state.baseRotation = 0;  // Reset base rotation!
      this.state.fineRotation = 0;  // Reset fine rotation!
      this.state.flip = { horizontal: false, vertical: false };
      this.state.adjustments = { brightness: 0, contrast: 0, saturation: 0 };
      this.state.preset = 'original';
      
      // Reset UI
      if (this.elements.rotationSlider) {
        this.elements.rotationSlider.value = 0;
      }
      if (this.elements.rotationValue) {
        this.elements.rotationValue.textContent = '0°';
      }
      this.elements.flipButtons.forEach(btn => btn.classList.remove('active'));
      this.resetAdjustments();
      
      // Reset transforms/filters
      this.elements.image.style.transform = '';
      this.elements.image.style.filter = '';
      this.elements.image.style.width = '';
      this.elements.image.style.height = '';
      
      // Reinitialize crop
      this.initCropTool();
      
      // Clear pending edits since user explicitly reset
      this.clearPendingEdits();
      
      console.log('[ImageEditor] Reset all edits');
    }

    // ========================================================================
    // NAVIGATION
    // ========================================================================
    goBack() {
      // Save pending edits to sessionStorage so they can be restored if user clicks edit again
      this.savePendingEdits();
      
      if (this.state.returnUrl) {
        window.location.href = this.state.returnUrl;
      } else {
        window.history.back();
      }
    }
    
    savePendingEdits() {
      // Save current edit state for restoration if user returns to editor
      // Crop coords are saved in effective (display) space - matches server processing
      const pendingEdits = {
        crop: {
          x: this.state.crop.x,
          y: this.state.crop.y,
          width: this.state.crop.width,
          height: this.state.crop.height
        },
        rotation: this.state.rotation,
        flip: this.state.flip,
        adjustments: this.state.adjustments,
        preset: this.state.preset
      };
      
      const key = `pendingEdits_${this.state.productHandle}_${this.state.slot}`;
      sessionStorage.setItem(key, JSON.stringify(pendingEdits));
      console.log('[ImageEditor] Saved pending edits to sessionStorage:', key, pendingEdits);
    }
    
    clearPendingEdits() {
      const key = `pendingEdits_${this.state.productHandle}_${this.state.slot}`;
      sessionStorage.removeItem(key);
      console.log('[ImageEditor] Cleared pending edits from sessionStorage:', key);
    }
    
    loadPendingEdits() {
      const key = `pendingEdits_${this.state.productHandle}_${this.state.slot}`;
      const pendingEditsStr = sessionStorage.getItem(key);
      
      if (pendingEditsStr) {
        try {
          const pendingEdits = JSON.parse(pendingEditsStr);
          console.log('[ImageEditor] Found pending edits in sessionStorage:', pendingEdits);
          return pendingEdits;
        } catch (e) {
          console.error('[ImageEditor] Failed to parse pending edits:', e);
          sessionStorage.removeItem(key);
        }
      }
      return null;
    }

    // ========================================================================
    // CONFIRMATION DIALOG
    // ========================================================================
    showConfirmDialog() {
      // === DEBUG DUMP - Compare overlay vs server calculations ===
      const rotation = this.state.rotation || 0;
      const baseRotation = Math.round(rotation / 90) * 90;
      const normalizedBase = ((baseRotation % 360) + 360) % 360;
      const isBaseRotated = (normalizedBase === 90 || normalizedBase === 270);
      
      const origW = this.state.originalWidth;
      const origH = this.state.originalHeight;
      const effectiveW = isBaseRotated ? origH : origW;
      const effectiveH = isBaseRotated ? origW : origH;
      
      const radians = Math.abs(rotation * Math.PI / 180);
      const cos = Math.abs(Math.cos(radians));
      const sin = Math.abs(Math.sin(radians));
      const rotatedW = Math.ceil(origW * cos + origH * sin);
      const rotatedH = Math.ceil(origW * sin + origH * cos);
      
      const offsetX = (rotatedW - effectiveW) / 2;
      const offsetY = (rotatedH - effectiveH) / 2;
      
      const serverCropX = Math.round(this.state.crop.x + offsetX);
      const serverCropY = Math.round(this.state.crop.y + offsetY);
      
      console.log('='.repeat(60));
      console.log('[DEBUG] CROP COORDINATE ANALYSIS');
      console.log('='.repeat(60));
      console.log('Original image:', origW, 'x', origH);
      console.log('Rotation:', rotation, '° (baseRotation:', normalizedBase, '°)');
      console.log('Effective dimensions:', effectiveW, 'x', effectiveH);
      console.log('Rotated canvas:', rotatedW, 'x', rotatedH);
      console.log('Offset (effective→rotated):', offsetX.toFixed(2), ',', offsetY.toFixed(2));
      console.log('---');
      console.log('Crop in EFFECTIVE space:', {
        x: this.state.crop.x.toFixed(2),
        y: this.state.crop.y.toFixed(2),
        w: this.state.crop.width.toFixed(2),
        h: this.state.crop.height.toFixed(2)
      });
      console.log('Crop in ROTATED CANVAS (what server extracts):', {
        x: serverCropX,
        y: serverCropY,
        w: Math.round(this.state.crop.width),
        h: Math.round(this.state.crop.height)
      });
      console.log('='.repeat(60));
      // === END DEBUG DUMP ===
      
      // Generate preview images
      this.elements.dialogOriginal.src = this.state.imageUrl;
      
      // Generate a proper cropped preview using canvas
      this.generateCroppedPreview();
      
      // Build changes list
      const changes = [];
      
      // Crop change
      const cropChanged = 
        this.state.crop.x !== 0 || 
        this.state.crop.y !== 0 ||
        this.state.crop.width !== this.state.originalWidth ||
        this.state.crop.height !== this.state.originalHeight;
      
      if (cropChanged) {
        changes.push(`Cropped to ${Math.round(this.state.crop.width)} × ${Math.round(this.state.crop.height)} pixels`);
      }
      
      if (this.state.rotation !== 0) {
        changes.push(`Rotated ${this.state.rotation}°`);
      }
      
      if (this.state.flip.horizontal) {
        changes.push('Flipped horizontally');
      }
      if (this.state.flip.vertical) {
        changes.push('Flipped vertically');
      }
      
      if (this.state.adjustments.brightness !== 0) {
        changes.push(`Brightness: ${this.state.adjustments.brightness > 0 ? '+' : ''}${this.state.adjustments.brightness}`);
      }
      if (this.state.adjustments.contrast !== 0) {
        changes.push(`Contrast: ${this.state.adjustments.contrast > 0 ? '+' : ''}${this.state.adjustments.contrast}`);
      }
      if (this.state.adjustments.saturation !== 0) {
        changes.push(`Saturation: ${this.state.adjustments.saturation > 0 ? '+' : ''}${this.state.adjustments.saturation}`);
      }
      
      if (this.state.preset !== 'original') {
        changes.push(`Preset: ${this.state.preset.charAt(0).toUpperCase() + this.state.preset.slice(1)}`);
      }
      
      if (changes.length === 0) {
        changes.push('No changes made');
      }
      
      this.elements.dialogChangesList.innerHTML = changes.map(c => `<li>${c}</li>`).join('');
      
      // Show low-res warning if applicable
      this.elements.dialogWarning.style.display = this.state.isLowRes ? 'flex' : 'none';
      
      // Reset checkbox
      this.elements.confirmCheckbox.checked = false;
      this.elements.dialogConfirmBtn.disabled = true;
      
      // Show dialog
      this.elements.confirmDialog.style.display = 'flex';
    }

    hideConfirmDialog() {
      this.elements.confirmDialog.style.display = 'none';
    }

    generateCroppedPreview() {
      // Server processes: rotate → crop → flip → adjustments
      // Preview shows EXACTLY what the final output will look like
      // Crop coords are in effective (post-base-rotation) space
      
      const rotation = this.state.rotation;
      const flipH = this.state.flip.horizontal;
      const flipV = this.state.flip.vertical;
      const crop = this.state.crop;
      
      // Calculate base rotation the SAME way as server:
      // Server uses: const baseRotation = Math.round(edits.rotation / 90) * 90;
      const baseRotation = Math.round(rotation / 90) * 90;
      const normalizedBase = ((baseRotation % 360) + 360) % 360;
      
      // Final output dimensions are the crop dimensions
      const outputW = Math.round(crop.width);
      const outputH = Math.round(crop.height);
      
      // Get effective dimensions (after base rotation) - matches server logic
      const isBaseRotated = (normalizedBase === 90 || normalizedBase === 270);
      const effectiveW = isBaseRotated ? this.state.originalHeight : this.state.originalWidth;
      const effectiveH = isBaseRotated ? this.state.originalWidth : this.state.originalHeight;
      
      console.log('[ImageEditor] generateCroppedPreview:', {
        crop,
        rotation,
        baseRotation: normalizedBase,
        effectiveSize: effectiveW + 'x' + effectiveH,
        flipH,
        flipV,
        output: outputW + 'x' + outputH
      });
      
      // Scale for preview
      const maxPreviewSize = 300;
      const scale = Math.min(maxPreviewSize / outputW, maxPreviewSize / outputH, 1);
      
      // Create the preview canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = Math.round(outputW * scale);
      canvas.height = Math.round(outputH * scale);
      
      // Fill with white background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const radians = (rotation * Math.PI) / 180;
      
      // Draw function: rotate whole image, then crop from it
      const drawPreview = (imgSource) => {
        // Create a temp canvas to rotate the original image
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        
        // Calculate rotated canvas size (bounding box after rotation)
        const origW = imgSource.naturalWidth || imgSource.width;
        const origH = imgSource.naturalHeight || imgSource.height;
        const cos = Math.abs(Math.cos(radians));
        const sin = Math.abs(Math.sin(radians));
        const rotatedW = Math.ceil(origW * cos + origH * sin);
        const rotatedH = Math.ceil(origW * sin + origH * cos);
        
        tempCanvas.width = rotatedW;
        tempCanvas.height = rotatedH;
        
        // Draw rotated image onto temp canvas (centered)
        tempCtx.fillStyle = '#ffffff';
        tempCtx.fillRect(0, 0, rotatedW, rotatedH);
        tempCtx.translate(rotatedW / 2, rotatedH / 2);
        tempCtx.rotate(radians);
        tempCtx.drawImage(imgSource, -origW / 2, -origH / 2);
        
        // The crop coordinates are in "effective" space (after base rotation)
        // The effective area is centered within the rotated canvas
        // Calculate the offset from rotated canvas to effective area
        const offsetX = (rotatedW - effectiveW) / 2;
        const offsetY = (rotatedH - effectiveH) / 2;
        
        // Actual crop position in the rotated temp canvas
        const actualCropX = Math.round(crop.x + offsetX);
        const actualCropY = Math.round(crop.y + offsetY);
        
        console.log('[ImageEditor] Preview crop offset:', {
          rotatedCanvas: rotatedW + 'x' + rotatedH,
          effective: effectiveW + 'x' + effectiveH,
          offset: offsetX.toFixed(1) + ', ' + offsetY.toFixed(1),
          cropInEffective: crop.x.toFixed(1) + ', ' + crop.y.toFixed(1),
          cropInRotated: actualCropX + ', ' + actualCropY
        });
        
        // Now crop from the rotated temp canvas and draw to output
        ctx.save();
        
        // Apply flips
        if (flipH || flipV) {
          ctx.translate(canvas.width / 2, canvas.height / 2);
          ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
          ctx.translate(-canvas.width / 2, -canvas.height / 2);
        }
        
        // Draw the crop area from rotated image (using corrected coordinates)
        ctx.drawImage(
          tempCanvas,
          actualCropX, actualCropY, outputW, outputH,  // Source: crop from rotated canvas
          0, 0, canvas.width, canvas.height  // Dest: fill preview canvas
        );
        
        ctx.restore();
        
        // Apply color adjustments
        this.applyAdjustmentsToCanvas(ctx, canvas.width, canvas.height);
        
        // Try to export
        try {
          const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
          this.elements.dialogEdited.src = dataUrl;
          this.elements.dialogEdited.style.transform = ''; // Clear any previous fallback
          console.log('[ImageEditor] Preview generated successfully:', canvas.width, 'x', canvas.height);
        } catch (e) {
          console.error('[ImageEditor] Canvas tainted (CORS):', e);
          throw e;
        }
      };
      
      // Try using the existing image first
      try {
        drawPreview(this.elements.image);
      } catch (e) {
        console.log('[ImageEditor] Retrying with fresh image load for CORS...');
        // Create a fresh image with explicit CORS
        const freshImg = new Image();
        freshImg.crossOrigin = 'anonymous';
        freshImg.onload = () => {
          try {
            drawPreview(freshImg);
          } catch (e2) {
            console.error('[ImageEditor] CORS failed on fresh load too:', e2);
            // Final fallback: show original with CSS rotation
            this.elements.dialogEdited.src = this.state.imageUrl;
            this.elements.dialogEdited.style.transform = `rotate(${rotation}deg) scale(0.8)`;
          }
        };
        freshImg.onerror = () => {
          console.error('[ImageEditor] Fresh image load failed');
          this.elements.dialogEdited.src = this.state.imageUrl;
          this.elements.dialogEdited.style.transform = `rotate(${rotation}deg) scale(0.8)`;
        };
        freshImg.src = this.state.imageUrl;
      }
    }
    
    applyAdjustmentsToCanvas(ctx, width, height) {
      const { brightness, contrast, saturation } = this.state.adjustments;
      if (brightness === 0 && contrast === 0 && saturation === 0) return;
      
      try {
        const imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;
        
        const brightnessMultiplier = 1 + (brightness / 100);
        const contrastFactor = (259 * (contrast + 255)) / (255 * (259 - contrast));
        const saturationMultiplier = 1 + (saturation / 100);
        
        for (let i = 0; i < data.length; i += 4) {
          let r = data[i];
          let g = data[i + 1];
          let b = data[i + 2];
          
          // Brightness
          r *= brightnessMultiplier;
          g *= brightnessMultiplier;
          b *= brightnessMultiplier;
          
          // Contrast
          r = contrastFactor * (r - 128) + 128;
          g = contrastFactor * (g - 128) + 128;
          b = contrastFactor * (b - 128) + 128;
          
          // Saturation
          const gray = 0.2989 * r + 0.587 * g + 0.114 * b;
          r = gray + saturationMultiplier * (r - gray);
          g = gray + saturationMultiplier * (g - gray);
          b = gray + saturationMultiplier * (b - gray);
          
          // Clamp values
          data[i] = Math.max(0, Math.min(255, r));
          data[i + 1] = Math.max(0, Math.min(255, g));
          data[i + 2] = Math.max(0, Math.min(255, b));
        }
        
        ctx.putImageData(imageData, 0, 0);
      } catch (e) {
        console.warn('[ImageEditor] Could not apply adjustments to canvas:', e);
      }
    }

    // ========================================================================
    // PROCESS AND SAVE (Send to SDOAC)
    // ========================================================================
    async processAndSave() {
      this.hideConfirmDialog();
      this.showProcessing('Processing your image...');
      
      try {
        // Server processes: rotate → crop → flip → adjustments
        // Crop coordinates sent in EFFECTIVE space; server adds offset
        const edits = {
          crop: {
            x: Math.round(this.state.crop.x),
            y: Math.round(this.state.crop.y),
            width: Math.round(this.state.crop.width),
            height: Math.round(this.state.crop.height)
          },
          rotation: this.state.rotation,
          flip: this.state.flip,
          adjustments: this.state.adjustments,
          preset: this.state.preset !== 'original' ? this.state.preset : null
        };
        
        console.log('[ImageEditor] Sending to server (effective coords):', edits);
        
        const response = await fetch(`${this.apiUrl}/api/process/apply-edits`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            originalUrl: this.state.imageUrl,
            shop: this.shopDomain,
            edits: edits
          })
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.error || 'Processing failed');
        }
        
        const result = await response.json();
        console.log('[ImageEditor] Processing complete:', result);
        
        this.showProcessing('Saving...');
        
        // Prepare data to send back - include requirements so they travel with the image
        const editData = {
          originalUrl: this.state.imageUrl,
          editedUrl: result.edited_url,
          edits: {
            ...edits,
            requirements: this.state.requirements || null
          },
          slot: this.state.slot,
          timestamp: new Date().toISOString()
        };
        
        // If editing from cart, update the cart line item
        if (this.state.isCartEdit && this.state.cartLineKey) {
          console.log('[ImageEditor] Updating cart line:', this.state.cartLineKey);
          
          // Include requirements in edit data so they travel with the image
          const editDataForCart = {
            ...edits,
            requirements: this.state.requirements || null
          };
          
          await this.updateCartLine(this.state.cartLineKey, {
            '_image_url': result.edited_url,
            '_original_image_url': this.state.imageUrl,
            '_edit_data': JSON.stringify(editDataForCart)
          });
        } else {
          // Store in sessionStorage for the product page to pick up
          const storageKey = `imageEdit_${this.state.productHandle}_${this.state.slot}`;
          sessionStorage.setItem(storageKey, JSON.stringify(editData));
        }
        
        // Clear pending edits since they've been applied
        this.clearPendingEdits();
        
        // Redirect back
        if (this.state.returnUrl) {
          window.location.href = this.state.returnUrl;
        } else {
          window.history.back();
        }
        
      } catch (error) {
        console.error('[ImageEditor] Processing error:', error);
        this.hideProcessing();
        alert('Failed to process image: ' + error.message);
      }
    }

    showProcessing(text) {
      this.elements.processingText.textContent = text;
      this.elements.processingOverlay.style.display = 'flex';
    }

    hideProcessing() {
      this.elements.processingOverlay.style.display = 'none';
    }

    // ========================================================================
    // RESTORE EDIT STATE
    // ========================================================================
    restoreEditState(edits) {
      console.log('[ImageEditor] Restoring edit state:', edits);
      
      // Restore rotation (including base and fine components)
      const rotation = edits.rotation || 0;
      const baseRotation = Math.round(rotation / 90) * 90; // Nearest 90°
      const fineRotation = rotation - baseRotation;
      
      if (rotation !== 0) {
        this.state.rotation = rotation;
        this.state.baseRotation = baseRotation;
        this.state.fineRotation = fineRotation;
        this.applyTransforms();
        
        // Update UI
        if (this.elements.rotationSlider) {
          this.elements.rotationSlider.value = fineRotation;
        }
        if (this.elements.rotationValue) {
          this.elements.rotationValue.textContent = `${rotation}°`;
        }
        console.log('[ImageEditor] Restored rotation:', { total: rotation, base: baseRotation, fine: fineRotation });
      }
      
      // Restore crop - coords are already in effective (display) space
      // (Server now does rotate → crop, so coords match what user sees)
      if (edits.crop) {
        this.state.crop = {
          x: edits.crop.x || 0,
          y: edits.crop.y || 0,
          width: edits.crop.width || this.state.originalWidth,
          height: edits.crop.height || this.state.originalHeight
        };
        
        this.state.cropInitialized = true;
        console.log('[ImageEditor] Restored crop (effective space):', this.state.crop);
      }
      
      // Restore flip
      if (edits.flip) {
        this.state.flip = {
          horizontal: edits.flip.horizontal || false,
          vertical: edits.flip.vertical || false
        };
        this.applyTransforms();
        // Update flip button states
        this.elements.flipButtons?.forEach(btn => {
          if (btn.dataset.flip === 'horizontal') {
            btn.classList.toggle('active', this.state.flip.horizontal);
          }
          if (btn.dataset.flip === 'vertical') {
            btn.classList.toggle('active', this.state.flip.vertical);
          }
        });
        console.log('[ImageEditor] Restored flip:', this.state.flip);
      }
      
      // Restore adjustments
      if (edits.adjustments) {
        if (edits.adjustments.brightness !== undefined) {
          this.state.adjustments.brightness = edits.adjustments.brightness;
          if (this.elements.brightnessSlider) {
            this.elements.brightnessSlider.value = edits.adjustments.brightness;
          }
        }
        if (edits.adjustments.contrast !== undefined) {
          this.state.adjustments.contrast = edits.adjustments.contrast;
          if (this.elements.contrastSlider) {
            this.elements.contrastSlider.value = edits.adjustments.contrast;
          }
        }
        if (edits.adjustments.saturation !== undefined) {
          this.state.adjustments.saturation = edits.adjustments.saturation;
          if (this.elements.saturationSlider) {
            this.elements.saturationSlider.value = edits.adjustments.saturation;
          }
        }
        this.applyFilters();
        console.log('[ImageEditor] Restored adjustments:', this.state.adjustments);
      }
      
      // Restore preset
      if (edits.preset && edits.preset !== 'original') {
        this.state.preset = edits.preset;
        this.updatePresetUI();
        console.log('[ImageEditor] Restored preset:', edits.preset);
      }
      
      console.log('[ImageEditor] Edit state restoration complete');
    }

    // ========================================================================
    // CART UPDATE
    // ========================================================================
    async updateCartLine(lineKey, properties) {
      console.log('[ImageEditor] Updating cart line properties:', lineKey, properties);
      
      try {
        // First, get the current cart to find the line item
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();
        
        const lineItem = cart.items.find(item => item.key === lineKey);
        if (!lineItem) {
          console.error('[ImageEditor] Cart line not found:', lineKey);
          throw new Error('Cart item not found');
        }
        
        // Merge existing properties with new ones
        const updatedProperties = { ...lineItem.properties, ...properties };
        
        // Update the cart line using the change endpoint
        const updateResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: lineKey,
            quantity: lineItem.quantity,
            properties: updatedProperties
          })
        });
        
        if (!updateResponse.ok) {
          throw new Error('Failed to update cart');
        }
        
        const result = await updateResponse.json();
        console.log('[ImageEditor] Cart updated successfully:', result);
        
        return result;
      } catch (error) {
        console.error('[ImageEditor] Cart update error:', error);
        throw error;
      }
    }

    // ========================================================================
    // UTILITY
    // ========================================================================
    gcd(a, b) {
      return b === 0 ? a : this.gcd(b, a % b);
    }
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================
  document.addEventListener('DOMContentLoaded', () => {
    const container = document.getElementById('image-editor');
    if (container) {
      window.imageEditor = new ImageEditor(container);
    }
  });

})();

